// ACTA_SHARE_MODAL_DIRECT_V46
// ACTA_SHARE_MODAL_MEETING_WORKFLOW_V1
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Copy,
  EnvelopeSimple,
  LinkSimple,
  PaperPlaneTilt,
  WhatsappLogo,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { prospects as prospectsApi, shares } from "@/api";
import { useAuth } from "@/context/AuthContext";
import * as shareSettingsApi from "@/api/shareSettings";
import {
  buildShareTemplateContext,
  renderShareTemplate,
  resolveShareSettings,
} from "@/lib/shareMessageTemplate";
import { formatApiError } from "@/lib/api";
import { buildMailComposePath } from "@/lib/mailCompose";

const DEFAULT_MEETING_SLOT = {
  date: "",
  start: "",
  durationMinutes: "30",
  locationType: "online",
  location: "",
};

function prospectName(prospect) {
  return (
    prospect?.company
    || `${prospect?.firstName || prospect?.first_name || ""} ${prospect?.lastName || prospect?.last_name || ""}`.trim()
    || prospect?.email
    || "Client"
  );
}

// ACTA_AFFAIRES_SECURE_SHARE_FLOW_V43
// ACTA_AFFAIRES_SECURE_SHARE_STEPS_V44
export default function ShareModal({
  item,
  onClose,
  defaultProspect = null,
  createShare = null,
  onGenerated = null,
  lockRecipient = false,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState(
    lockRecipient
      ? "preferences"
      : "recipient"
  );
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedProspect, setSelectedProspect] = useState(defaultProspect);
  const [creatingProspect, setCreatingProspect] = useState(false);
  const [newProspect, setNewProspect] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    city: "",
    email: "",
  });
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [maxOpens, setMaxOpens] = useState(5);
  const [allowDownload, setAllowDownload] = useState(false);
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [welcomeMessageEnabled, setWelcomeMessageEnabled] = useState(true);
  const [welcomeMessageTouched, setWelcomeMessageTouched] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [proposeMeetingWithShare, setProposeMeetingWithShare] = useState(false);
  const [meetingSlot, setMeetingSlot] = useState(DEFAULT_MEETING_SLOT);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [myShareSettings, setMyShareSettings] = useState({});
  const [tenantShareSettings, setTenantShareSettings] = useState({});
  const [tenantName, setTenantName] = useState("");
  const [outboundSubject, setOutboundSubject] = useState("");
  const [outboundMessage, setOutboundMessage] = useState("");
  const [outboundMessageTouched, setOutboundMessageTouched] = useState(false);

  // ACTA_SHARE_MODAL_DYNAMIC_TITLE_V1
  // Composant unique Commerce + Documents.
  const shareEntity = useMemo(() => {
    if (item?.type === "quote") {
      return {
        action: "Partager le devis",
        result: "Devis partagé",
      };
    }

    if (item?.type === "folder") {
      return {
        action: "Partager le dossier",
        result: "Dossier partagé",
      };
    }

    if (
      item?.type === "selection"
      || (Array.isArray(item?.items) && item.items.length > 1)
    ) {
      const selectionItems = Array.isArray(item?.items)
        ? item.items
        : [];

      const folderCount = selectionItems.filter(
        (entry) => entry?.type === "folder"
      ).length;

      const documentCount = selectionItems.filter(
        (entry) => entry?.type === "document"
      ).length;

      if (folderCount > 0 && documentCount === 0) {
        return {
          action: "Partager les dossiers",
          result: "Dossiers partagés",
        };
      }

      if (folderCount > 0 && documentCount > 0) {
        return {
          action: "Partager la sélection",
          result: "Sélection partagée",
        };
      }

      return {
        action: "Partager les documents",
        result: "Documents partagés",
      };
    }

    return {
      action: "Partager le document",
      result: "Document partagé",
    };
  }, [item?.items, item?.type]);

  const shareModalTitle =
    mode === "result"
      ? shareEntity.result
      : shareEntity.action;

  const defaultProspectRef = useRef(defaultProspect);
  defaultProspectRef.current = defaultProspect;

  const shareContextKey = [
    item?.type || "",
    item?.id || "",
    defaultProspect?.id || "",
  ].join(":");

  useEffect(() => {
    prospectsApi.list().then(setProspects).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;

    Promise.all([
      shareSettingsApi
        .getMine()
        .catch(() => ({ settings: {} })),
      shareSettingsApi
        .getTenant()
        .catch(() => ({
          settings: {},
          tenantName: user?.tenant?.name || "",
        })),
    ]).then(([mineResult, tenantResult]) => {
      if (!mounted) return;

      setMyShareSettings(
        mineResult?.settings || {}
      );

      setTenantShareSettings(
        tenantResult?.settings || {}
      );

      setTenantName(
        tenantResult?.tenantName
        || user?.tenant?.name
        || ""
      );
    });

    return () => {
      mounted = false;
    };
  }, [user?.tenant?.name]);

  useEffect(() => {
    setSelectedProspect(defaultProspectRef.current);
    setMode(
      lockRecipient
        ? "preferences"
        : "recipient"
    );
    setSearch("");
    setCreatingProspect(false);
    setNewProspect({
      first_name: "",
      last_name: "",
      phone: "",
      city: "",
      email: "",
    });
    setExpiresInDays(7);
    setMaxOpens(5);
    setAllowDownload(false);
    setWatermarkEnabled(true);
    setWelcomeMessageEnabled(true);
    setWelcomeMessageTouched(false);
    setWelcomeMessage("");
    setProposeMeetingWithShare(false);
    setMeetingSlot(DEFAULT_MEETING_SLOT);
    setShare(null);
    setCopied(false);
    setOutboundSubject("");
    setOutboundMessage("");
    setOutboundMessageTouched(false);
  }, [lockRecipient, shareContextKey]);

  const sharedItemNames = useMemo(() => {
    const rawItems = item?.items?.length ? item.items : [item];
    return rawItems
      .map(
        (entry) =>
          entry?.name
          || entry?.title
          || entry?.filename
          || item?.name
          || "Document partagé"
      )
      .filter(Boolean);
  }, [item]);

  const effectiveShareSettings = useMemo(
    () =>
      resolveShareSettings(
        myShareSettings,
        tenantShareSettings
      ),
    [
      myShareSettings,
      tenantShareSettings,
    ]
  );

  const baseTemplateContext = useMemo(
    () =>
      buildShareTemplateContext({
        prospect: selectedProspect,
        user,
        tenantName,
        documents: sharedItemNames,
      }),
    [
      selectedProspect,
      user,
      tenantName,
      sharedItemNames,
    ]
  );

  const defaultWelcomeMessage = useMemo(() => {
    let rendered = renderShareTemplate(
      effectiveShareSettings.welcomeMessage,
      baseTemplateContext
    );

    if (proposeMeetingWithShare) {
      rendered = [
        rendered,
        "",
        "Je vous ai aussi proposé un rendez-vous que vous pourrez confirmer depuis votre espace.",
      ].join("\n");
    }

    return rendered;
  }, [
    effectiveShareSettings.welcomeMessage,
    baseTemplateContext,
    proposeMeetingWithShare,
  ]);

  useEffect(() => {
    if (!welcomeMessageTouched) {
      setWelcomeMessage(defaultWelcomeMessage);
    }
  }, [defaultWelcomeMessage, welcomeMessageTouched]);

  const filteredProspects = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fr-FR");
    if (!query) return prospects;

    return prospects.filter((prospect) =>
      [
        prospectName(prospect),
        prospect?.city,
        prospect?.phone,
        prospect?.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("fr-FR")
        .includes(query)
    );
  }, [prospects, search]);

  const createProspect = async () => {
    try {
      const prospect = await prospectsApi.create(newProspect);
      setProspects((current) => [prospect, ...current]);
      setSelectedProspect(prospect);
      setCreatingProspect(false);
      setMode("preferences");
      toast.success("Prospect créé");
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Création du prospect impossible."
      );
    }
  };

  const validMeetingSlot = useMemo(() => ({
    date: String(meetingSlot.date || "").trim(),
    start: String(meetingSlot.start || "").trim(),
    durationMinutes: Number(meetingSlot.durationMinutes || 30),
    locationType: String(meetingSlot.locationType || "online").trim(),
    location: String(meetingSlot.location || "").trim(),
  }), [meetingSlot]);

  const validateMeetingSelection = () => {
    if (!proposeMeetingWithShare) return true;

    if (
      !validMeetingSlot.date
      || !validMeetingSlot.start
      || (
        validMeetingSlot.locationType === "physical"
        && !validMeetingSlot.location
      )
    ) {
      toast.error("Complétez le rendez-vous proposé");
      return false;
    }

    return true;
  };

  const continueFromMeeting = () => {
    if (validateMeetingSelection()) {
      setMode("security");
    }
  };

  const generate = async () => {
    if (!selectedProspect?.id) {
      toast.error("Sélectionnez un prospect");
      return;
    }

    if (!validateMeetingSelection()) return;

    setLoading(true);

    try {
      const payload = {
        prospect_id: selectedProspect.id,
        items: item.items || [{ type: item.type, id: item.id }],
        expires_in_days: Number(expiresInDays),
        max_opens: Number(maxOpens),
        allow_download: allowDownload,
        watermark_enabled: watermarkEnabled,
        notify_on_open: true,
      };

      const result = createShare
        ? await createShare(payload, selectedProspect)
        : await shares.create(payload);
      const createdShare = result?.share || result;

      setShare(createdShare);
      setMode("result");

      if (createdShare?.id) {
        try {
          if (welcomeMessageEnabled && welcomeMessage.trim()) {
            await shares.sendExchangeMessage(createdShare.id, {
              content: welcomeMessage.trim(),
            });
          }

          if (proposeMeetingWithShare) {
            await shares.proposeExchangeMeetings(createdShare.id, {
              slots: [validMeetingSlot],
            });
          }
        } catch {
          toast.error(
            "Partage créé, mais message ou rendez-vous impossible à ajouter"
          );
        }
      }

      await onGenerated?.(createdShare, result);
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
        || "Création du partage impossible."
      );
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = share
    ? `${window.location.origin}/share/${share.token}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const normalizePhoneForWhatsapp = (phone = "") => {
    let clean = phone.replace(/[^\d+]/g, "");
    if (clean.startsWith("+")) clean = clean.substring(1);
    if (clean.startsWith("00")) clean = clean.substring(2);
    if (clean.startsWith("0")) clean = `33${clean.substring(1)}`;
    return clean;
  };

  const outboundDefaults = useMemo(() => {
    const context = buildShareTemplateContext({
      prospect: selectedProspect,
      user,
      tenantName,
      documents: sharedItemNames,
      shareUrl,
    });

    return {
      subject: renderShareTemplate(
        effectiveShareSettings.emailSubject,
        context
      ),
      message: renderShareTemplate(
        effectiveShareSettings.outboundMessage,
        context
      ),
    };
  }, [
    selectedProspect,
    user,
    tenantName,
    sharedItemNames,
    shareUrl,
    effectiveShareSettings.emailSubject,
    effectiveShareSettings.outboundMessage,
  ]);

  useEffect(() => {
    if (
      !share
      || outboundMessageTouched
    ) {
      return;
    }

    setOutboundSubject(
      outboundDefaults.subject
    );
    setOutboundMessage(
      outboundDefaults.message
    );
  }, [
    share,
    outboundDefaults,
    outboundMessageTouched,
  ]);

  const whatsappPhone = normalizePhoneForWhatsapp(
    selectedProspect?.phone || ""
  );
  const whatsappUrl = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(outboundMessage)}`;
  const internalMailPath = buildMailComposePath({
    to: selectedProspect?.email || "",
    prospectId: selectedProspect?.id || "",
    subject: outboundSubject,
    body: outboundMessage,
  });

  const shareSteps = [
    { key: "recipient", label: "Destinataire" },
    { key: "preferences", label: "Préférences" },
    { key: "message", label: "Message" },
    { key: "meeting", label: "Rendez-vous" },
    { key: "security", label: "Sécurité" },
  ];

  const currentStepIndex = shareSteps.findIndex(
    (step) => step.key === mode
  );

  const previousStep = {
    preferences: "recipient",
    message: "preferences",
    meeting: "message",
    security: "meeting",
  }[mode];

  const continueStep = () => {
    if (mode === "preferences") {
      setMode("message");
      return;
    }

    if (mode === "message") {
      setMode("meeting");
      return;
    }

    if (mode === "meeting") {
      continueFromMeeting();
    }
  };

  const closeModal = () => {
    if (!loading) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center acta-modal-overlay p-3 sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeModal();
      }}
      data-testid="share-modal"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        className="acta-modal-surface flex max-h-[calc(100dvh-1.5rem)] w-full max-w-[640px] flex-col overflow-hidden rounded-3xl bg-[var(--do-bg-solid)] shadow-2xl sm:max-h-[calc(100dvh-3rem)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 px-4 pb-3 pt-4 sm:px-5 sm:pt-5">
          <div className="min-w-0">
            <h2
              id="share-modal-title"
              className="truncate font-heading text-2xl font-black text-[var(--do-ink)]"
            >
              {shareModalTitle}
            </h2>
            {sharedItemNames.length > 1 ? (
              <div className="mt-2" data-testid="share-selected-documents">
                <p className="text-sm font-bold text-[var(--do-muted)]">
                  {sharedItemNames.length} documents sélectionnés
                </p>
                <ul className="mt-1 max-h-28 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-[var(--do-ink)]">
                  {sharedItemNames.map((name, index) => (
                    <li key={`${index}-${name}`} className="break-words">{name}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="mt-1 truncate text-sm font-bold text-[var(--do-muted)]">
                {item.name}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={closeModal}
            disabled={loading}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] disabled:opacity-50"
            aria-label="Fermer"
            data-testid="share-close"
          >
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-5 sm:pb-5">
          {mode !== "result" && currentStepIndex >= 0 && (
            <div
              className="mb-4 border-t border-[var(--do-border)] pt-4"
              data-testid="share-step-progress"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Étape {currentStepIndex + 1} sur {shareSteps.length}
                </span>
                <strong className="text-xs font-black text-[var(--do-ink)]">
                  {shareSteps[currentStepIndex].label}
                </strong>
              </div>

              <div className="mt-2 grid grid-cols-5 gap-1.5" aria-hidden="true">
                {shareSteps.map((step, index) => (
                  <span
                    key={step.key}
                    className={`h-1.5 rounded-full ${
                      index <= currentStepIndex
                        ? "bg-[var(--do-accent)]"
                        : "bg-[var(--do-border)]"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {mode === "recipient" && (
            <div className="space-y-4">
              <section className="space-y-2 border-t border-[var(--do-border)] pt-4">
                <h3 className="font-heading text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
                  {creatingProspect ? "Créer un prospect" : "Destinataire"}
                </h3>

                {!creatingProspect ? (
                  <>
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Rechercher un destinataire…"
                      className="min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-border-strong)]"
                      data-testid="share-prospect-search"
                    />

                    <div
                      className="max-h-64 overflow-y-auto rounded-xl border border-[var(--do-border)]"
                      data-testid="share-prospect-list"
                    >
                      {filteredProspects.map((prospect) => (
                        <button
                          key={prospect.id}
                          type="button"
                          onClick={() => setSelectedProspect(prospect)}
                          data-testid={`share-pick-prospect-${prospect.id}`}
                          className={`flex min-h-12 w-full items-center justify-between gap-3 border-b border-[var(--do-border)] px-3 text-left last:border-b-0 hover:bg-[var(--do-surface-soft)] ${selectedProspect?.id === prospect.id ? "bg-[var(--do-accent-soft)]" : ""}`}
                        >
                          <span className="min-w-0">
                            <strong className="block truncate text-sm text-[var(--do-ink)]">
                              {prospectName(prospect)}
                            </strong>
                            <small className="block truncate text-xs text-[var(--do-muted)]">
                              {[prospect.email, prospect.phone]
                                .filter(Boolean)
                                .join(" · ")}
                            </small>
                          </span>
                          {selectedProspect?.id === prospect.id && (
                            <Check size={17} weight="bold" className="text-[var(--do-accent)]" />
                          )}
                        </button>
                      ))}

                      {!filteredProspects.length && (
                        <div className="p-4 text-center text-sm text-[var(--do-muted)]">
                          Aucun destinataire trouvé.
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setCreatingProspect(true)}
                      className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)]"
                      data-testid="share-new-prospect-btn"
                    >
                      + Nouveau prospect
                    </button>
                  </>
                ) : (
                  <div className="space-y-3" data-testid="share-new-prospect-form">
                    {[
                      ["first_name", "Prénom *"],
                      ["last_name", "Nom *"],
                      ["phone", "Téléphone *"],
                      ["city", "Ville *"],
                      ["email", "Email"],
                    ].map(([key, label]) => (
                      <label key={key} className="block">
                        <span className="text-xs font-bold text-[var(--do-muted)]">{label}</span>
                        <input
                          value={newProspect[key]}
                          onChange={(event) =>
                            setNewProspect((current) => ({
                              ...current,
                              [key]: event.target.value,
                            }))
                          }
                          className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] px-3 text-sm"
                          data-testid={`new-prospect-${key}`}
                        />
                      </label>
                    ))}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setCreatingProspect(false)}
                        className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={createProspect}
                        className="min-h-11 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)]"
                        data-testid="confirm-new-prospect"
                      >
                        Créer
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}

          {mode === "preferences" && (
            <div
              className="space-y-4"
              data-testid="share-preferences-step"
            >
              <section className="space-y-3">
                <div>
                  <h3 className="font-heading text-lg font-black text-[var(--do-ink)]">
                    Préférences de partage
                  </h3>
                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    Définissez comment le client pourra consulter le document.
                  </p>
                </div>

                <div
                  className="grid gap-2 sm:grid-cols-3"
                  data-testid="share-options-aligned"
                >
                  <label
                    className={`flex min-h-24 cursor-pointer flex-col justify-between rounded-2xl border p-3 ${
                      watermarkEnabled
                        ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                        : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <strong className="text-sm text-[var(--do-ink)]">
                        Filigrane dynamique
                      </strong>
                      <input
                        type="checkbox"
                        checked={watermarkEnabled}
                        onChange={(event) =>
                          setWatermarkEnabled(event.target.checked)
                        }
                        data-testid="share-watermark"
                        className="h-4 w-4 accent-[var(--do-accent)]"
                      />
                    </span>
                    <small className="mt-2 text-xs text-[var(--do-muted)]">
                      Identification du destinataire sur le document.
                    </small>
                  </label>

                  <div className="flex min-h-24 flex-col justify-between rounded-2xl border border-[var(--do-accent)] bg-[var(--do-accent-soft)] p-3">
                    <span className="flex items-center justify-between gap-2">
                      <strong className="text-sm text-[var(--do-ink)]">
                        Lecture seule
                      </strong>
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        disabled
                        data-testid="share-read-only"
                        className="h-4 w-4 accent-[var(--do-accent)]"
                      />
                    </span>
                    <small className="mt-2 text-xs text-[var(--do-muted)]">
                      Consultation sécurisée dans l’espace client.
                    </small>
                  </div>

                  <label
                    className={`flex min-h-24 cursor-pointer flex-col justify-between rounded-2xl border p-3 ${
                      allowDownload
                        ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                        : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <strong className="text-sm text-[var(--do-ink)]">
                        Téléchargement
                      </strong>
                      <input
                        type="checkbox"
                        checked={allowDownload}
                        onChange={(event) =>
                          setAllowDownload(event.target.checked)
                        }
                        data-testid="share-allow-download"
                        className="h-4 w-4 accent-[var(--do-accent)]"
                      />
                    </span>
                    <small className="mt-2 text-xs text-[var(--do-muted)]">
                      Autoriser le client à récupérer le fichier.
                    </small>
                  </label>
                </div>

                <div
                  className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-3 py-3 text-xs leading-5 text-[var(--do-muted)]"
                  data-testid="share-tracking-active"
                >
                  <strong className="text-[var(--do-ink)]">
                    Traçabilité active.
                  </strong>{" "}
                  Vous pourrez suivre les ouvertures et consultations du partage.
                </div>
              </section>
            </div>
          )}

          {mode === "message" && (
            <div
              className="space-y-4"
              data-testid="share-message-step"
            >
              <section
                className="space-y-3"
                data-testid="share-discussion-meeting-options"
              >
                <div>
                  <h3 className="font-heading text-lg font-black text-[var(--do-ink)]">
                    Message d’accueil
                  </h3>
                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    Communication avec le client
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setWelcomeMessageEnabled((current) => !current)
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                    welcomeMessageEnabled
                      ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                      : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"
                  }`}
                  data-testid="share-welcome-message-enabled"
                  aria-pressed={welcomeMessageEnabled}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span>
                      <strong className="block text-sm text-[var(--do-ink)]">
                        Ajouter un message
                      </strong>
                      <small className="mt-1 block text-xs text-[var(--do-muted)]">
                        Le message apparaîtra dans l’espace de partage.
                      </small>
                    </span>
                    <Check
                      size={18}
                      weight="bold"
                      className={
                        welcomeMessageEnabled
                          ? "text-[var(--do-accent)]"
                          : "text-[var(--do-muted)]"
                      }
                    />
                  </span>
                </button>

                {welcomeMessageEnabled && (
                  <textarea
                    value={welcomeMessage}
                    onChange={(event) => {
                      setWelcomeMessageTouched(true);
                      setWelcomeMessage(event.target.value);
                    }}
                    rows={7}
                    className="w-full resize-none rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm leading-6 outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="share-welcome-message"
                  />
                )}
              </section>
            </div>
          )}

          {mode === "meeting" && (
            <div
              className="space-y-4"
              data-testid="share-meeting-step"
            >
              <section className="space-y-3">
                <div>
                  <h3 className="font-heading text-lg font-black text-[var(--do-ink)]">
                    Rendez-vous
                  </h3>
                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    Vous pouvez proposer un créneau avec le partage.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setProposeMeetingWithShare((current) => !current)
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                    proposeMeetingWithShare
                      ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                      : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"
                  }`}
                  data-testid="share-propose-meeting-enabled"
                  aria-pressed={proposeMeetingWithShare}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span>
                      <strong className="block text-sm text-[var(--do-ink)]">
                        Proposer un rendez-vous
                      </strong>
                      <small className="mt-1 block text-xs text-[var(--do-muted)]">
                        Le client pourra confirmer le créneau proposé.
                      </small>
                    </span>
                    <Check
                      size={18}
                      weight="bold"
                      className={
                        proposeMeetingWithShare
                          ? "text-[var(--do-accent)]"
                          : "text-[var(--do-muted)]"
                      }
                    />
                  </span>
                </button>

                {proposeMeetingWithShare && (
                  <div
                    className="grid gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3 sm:grid-cols-2"
                    data-testid="share-meeting-slots"
                  >
                    <label>
                      <span className="text-xs font-bold text-[var(--do-muted)]">
                        Date
                      </span>
                      <input
                        type="date"
                        value={meetingSlot.date}
                        onChange={(event) =>
                          setMeetingSlot((current) => ({
                            ...current,
                            date: event.target.value,
                          }))
                        }
                        className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                        data-testid="share-meeting-date-0"
                      />
                    </label>

                    <label>
                      <span className="text-xs font-bold text-[var(--do-muted)]">
                        Heure
                      </span>
                      <input
                        type="time"
                        value={meetingSlot.start}
                        onChange={(event) =>
                          setMeetingSlot((current) => ({
                            ...current,
                            start: event.target.value,
                          }))
                        }
                        className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                        data-testid="share-meeting-start-0"
                      />
                    </label>

                    <label>
                      <span className="text-xs font-bold text-[var(--do-muted)]">
                        Durée
                      </span>
                      <select
                        value={meetingSlot.durationMinutes}
                        onChange={(event) =>
                          setMeetingSlot((current) => ({
                            ...current,
                            durationMinutes: event.target.value,
                          }))
                        }
                        className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                        data-testid="share-meeting-duration-0"
                      >
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="45">45 minutes</option>
                        <option value="60">1 heure</option>
                      </select>
                    </label>

                    <label>
                      <span className="text-xs font-bold text-[var(--do-muted)]">
                        Format
                      </span>
                      <select
                        value={meetingSlot.locationType}
                        onChange={(event) =>
                          setMeetingSlot((current) => ({
                            ...current,
                            locationType: event.target.value,
                          }))
                        }
                        className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                        data-testid="share-meeting-location-type-0"
                      >
                        <option value="online">En ligne</option>
                        <option value="physical">Sur place</option>
                      </select>
                    </label>

                    {meetingSlot.locationType === "physical" ? (
                      <label className="sm:col-span-2">
                        <span className="text-xs font-bold text-[var(--do-muted)]">
                          Adresse
                        </span>
                        <input
                          value={meetingSlot.location}
                          onChange={(event) =>
                            setMeetingSlot((current) => ({
                              ...current,
                              location: event.target.value,
                            }))
                          }
                          className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
                          data-testid="share-meeting-location-0"
                        />
                      </label>
                    ) : (
                      <div
                        className="rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs text-[var(--do-accent)] sm:col-span-2"
                        data-testid="share-meeting-online-0"
                      >
                        Le lien de visioconférence pourra être ajouté après confirmation.
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>
          )}

          {mode === "security" && (
            <div
              className="space-y-4"
              data-testid="share-security-step"
            >
              <section className="space-y-3">
                <div>
                  <h3 className="font-heading text-lg font-black text-[var(--do-ink)]">
                    Sécurité du lien
                  </h3>
                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    Définissez la durée et le nombre d’ouvertures autorisées.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Expiration
                    </span>
                    <select
                      value={expiresInDays}
                      onChange={(event) =>
                        setExpiresInDays(event.target.value)
                      }
                      className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm font-bold"
                      data-testid="share-expires-input"
                    >
                      {[1, 3, 7, 14, 30, 60, 90].map((days) => (
                        <option key={days} value={days}>
                          {days} jour{days > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Ouvertures max
                    </span>
                    <select
                      value={maxOpens}
                      onChange={(event) =>
                        setMaxOpens(event.target.value)
                      }
                      className="mt-1 min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm font-bold"
                      data-testid="share-maxopens-input"
                    >
                      {[1, 3, 5, 10, 20, 50].map((count) => (
                        <option key={count} value={count}>
                          {count} ouverture{count > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] px-3 py-3 text-xs leading-5 text-[var(--do-muted)]">
                  Filigrane{" "}
                  <strong className="text-[var(--do-ink)]">
                    {watermarkEnabled ? "activé" : "désactivé"}
                  </strong>
                  {" · "}
                  Téléchargement{" "}
                  <strong className="text-[var(--do-ink)]">
                    {allowDownload ? "autorisé" : "bloqué"}
                  </strong>
                  {" · "}
                  Traçabilité{" "}
                  <strong className="text-[var(--do-ink)]">
                    active
                  </strong>
                </div>
              </section>
            </div>
          )}

          {mode === "result" && share && (
            <div className="space-y-4 border-t border-[var(--do-border)] pt-4">
              <section className="rounded-2xl bg-[var(--do-accent-soft)] p-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--do-accent)] text-[var(--do-on-accent)]">
                    <Check size={18} weight="bold" />
                  </span>
                  <div>
                    <strong className="block text-sm text-[var(--do-ink)]">Partage sécurisé créé</strong>
                    <small className="mt-1 block text-xs text-[var(--do-muted)]">Le partage est disponible via le lien sécurisé.</small>
                  </div>
                </div>
              </section>

              <div className="flex items-center gap-2 rounded-xl border border-[var(--do-border)] p-2">
                <LinkSimple size={17} className="shrink-0 text-[var(--do-muted)]" />
                <input
                  readOnly
                  value={shareUrl}
                  className="min-w-0 flex-1 truncate bg-transparent text-xs outline-none"
                  data-testid="share-url-input"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-[var(--do-border)] px-3 text-xs font-black"
                  data-testid="share-copy-btn"
                >
                  {copied ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier</>}
                </button>
              </div>

              <section
                className="space-y-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3"
                data-testid="share-outbound-message-editor"
              >
                <div>
                  <strong className="block text-sm text-[var(--do-ink)]">
                    Message à envoyer
                  </strong>
                  <small className="mt-1 block text-xs text-[var(--do-muted)]">
                    Relisez et modifiez le message avant d’ouvrir votre e-mail ou WhatsApp.
                  </small>
                </div>

                <label className="block">
                  <span className="text-xs font-bold text-[var(--do-muted)]">
                    Objet e-mail
                  </span>
                  <input
                    value={outboundSubject}
                    onChange={(event) => {
                      setOutboundMessageTouched(true);
                      setOutboundSubject(event.target.value);
                    }}
                    className="mt-1 min-h-10 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="share-outbound-subject"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-bold text-[var(--do-muted)]">
                    Message
                  </span>
                  <textarea
                    value={outboundMessage}
                    onChange={(event) => {
                      setOutboundMessageTouched(true);
                      setOutboundMessage(event.target.value);
                    }}
                    rows={10}
                    className="mt-1 w-full resize-y rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm leading-6 outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="share-outbound-message"
                  />
                </label>

                <button
                  type="button"
                  onClick={() => {
                    setOutboundMessageTouched(false);
                    setOutboundSubject(outboundDefaults.subject);
                    setOutboundMessage(outboundDefaults.message);
                  }}
                  className="min-h-9 rounded-lg border border-[var(--do-border)] px-3 text-xs font-black text-[var(--do-ink)]"
                  data-testid="share-reset-outbound-message"
                >
                  Réinitialiser avec mon modèle
                </button>
              </section>

              <div className="grid gap-2 sm:grid-cols-2">
                <a
                  href={whatsappPhone ? whatsappUrl : "#"}
                  onClick={(event) => {
                    if (!whatsappPhone) {
                      event.preventDefault();
                      toast.error("Aucun numéro WhatsApp valide pour ce prospect");
                    }
                  }}
                  target="_blank"
                  rel="noreferrer"
                  className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] text-sm font-black text-[var(--do-ink)]"
                  data-testid="share-whatsapp-btn"
                >
                  <WhatsappLogo size={17} weight="fill" /> WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedProspect?.email) {
                      toast.error("Aucun email pour ce prospect");
                      return;
                    }

                    navigate(internalMailPath);
                  }}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] text-sm font-black text-[var(--do-ink)]"
                  data-testid="share-email-btn"
                >
                  <EnvelopeSimple size={17} weight="fill" /> E-mail
                </button>
              </div>
            </div>
          )}
        </div>

        {mode === "recipient" && !creatingProspect && (
          <footer className="grid shrink-0 gap-2 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:grid-cols-2 sm:px-5">
            <button
              type="button"
              onClick={closeModal}
              className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)]"
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={() => setMode("preferences")}
              disabled={!selectedProspect}
              className="min-h-11 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="share-next-btn"
            >
              Continuer
            </button>
          </footer>
        )}

        {["preferences", "message", "meeting", "security"].includes(mode) && (
          <footer className="grid shrink-0 gap-2 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:grid-cols-2 sm:px-5">
            <button
              type="button"
              onClick={() => setMode(previousStep)}
              disabled={loading}
              className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
              data-testid="share-back-btn"
            >
              Retour
            </button>

            {mode === "security" ? (
              <button
                type="button"
                onClick={generate}
                disabled={loading}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-60"
                data-testid="share-generate-btn"
              >
                <PaperPlaneTilt size={17} weight="bold" />
                {loading ? "Création…" : "Créer le partage"}
              </button>
            ) : (
              <button
                type="button"
                onClick={continueStep}
                className="min-h-11 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)]"
                data-testid="share-step-next-btn"
              >
                Continuer
              </button>
            )}
          </footer>
        )}

        {mode === "result" && (
          <footer className="shrink-0 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:px-5">
            <button
              type="button"
              onClick={closeModal}
              className="min-h-11 w-full rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)]"
              data-testid="share-done-btn"
            >
              Terminer
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}
